/**
 * JoinShare (FR-2.9) — the shareable on-ramp for a game.
 *
 * Renders a scannable QR code of the public player-join deep link
 * (`/player?code=<joinCode>`, see {@link playerJoinUrl}) alongside the
 * human-readable join code and a copy-link button, so a player can either scan
 * with a phone or type the code. Purely presentational: it takes a join code and
 * derives everything from it — no data fetching, no gating (the pages decide when
 * to show it, only when a shareable join code exists in prod).
 *
 * Used on the Host lobby banner (compact) and the Presentation lobby screen
 * (feature-sized, the shared-TV "grab your phone and scan" surface).
 */

import { useEffect, useRef, useState } from 'react';
import QRCode from 'react-qr-code';
import { playerJoinUrl } from '../net/join-url';

interface JoinShareProps {
  joinCode: string;
  /**
   * Visual emphasis. `feature` (default) is the large presentation/TV treatment;
   * `compact` is the smaller host-banner treatment; `qr-only` (#246 demo invite) shows
   * ONLY the scannable QR + a short caption — no code text, URL, or copy button — for a
   * frictionless "scan to join" on the demo play screen.
   */
  variant?: 'feature' | 'compact' | 'qr-only';
  /** Caption shown under the QR in the `qr-only` variant. */
  caption?: string;
}

/** True when the Clipboard API is usable in this environment (prod https / dev). */
function canCopy(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    typeof navigator.clipboard?.writeText === 'function'
  );
}

export function JoinShare({
  joinCode,
  variant = 'feature',
  caption = 'Scan to join — no account needed',
}: JoinShareProps): React.JSX.Element {
  const url = playerJoinUrl(joinCode);
  // Hooks run unconditionally (Rules of Hooks) — the `qr-only` branch below simply
  // doesn't use the copy state.
  const [copied, setCopied] = useState(false);
  const resetTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // Clear the transient "Copied!" flag timer on unmount so we never setState on
  // an unmounted component.
  useEffect(() => {
    return () => {
      if (resetTimer.current !== undefined) clearTimeout(resetTimer.current);
    };
  }, []);

  // QR-only (#246 demo invite): just the scannable code + a caption. No join code text,
  // URL, or copy button — a friend scans and lands on the guest join deep link.
  if (variant === 'qr-only') {
    return (
      <div className="join-share join-share--qr-only" data-testid="join-share">
        <div className="join-share__qr" data-testid="join-share-qr">
          <QRCode
            value={url}
            size={132}
            style={{ height: 'auto', maxWidth: '100%', width: '100%' }}
            aria-label="QR code to join this game"
          />
        </div>
        <span className="join-share__caption">{caption}</span>
      </div>
    );
  }

  const onCopy = (): void => {
    if (!canCopy()) return;
    void navigator.clipboard
      .writeText(url)
      .then(() => {
        setCopied(true);
        if (resetTimer.current !== undefined) clearTimeout(resetTimer.current);
        resetTimer.current = setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {
        // Clipboard denied/failed — leave the URL visible for manual copy.
      });
  };

  return (
    <div className={`join-share join-share--${variant}`} data-testid="join-share">
      <div className="join-share__qr" data-testid="join-share-qr">
        {/* Encodes the public join URL only — no PII (FR-2.9). */}
        <QRCode
          value={url}
          size={variant === 'compact' ? 112 : 176}
          // Let CSS scale it responsively within its box while staying crisp (SVG).
          style={{ height: 'auto', maxWidth: '100%', width: '100%' }}
          aria-label="QR code to join this game"
        />
      </div>
      <div className="join-share__details">
        <span className="join-share__label">Scan to join, or use code</span>
        <strong className="join-share__code" data-testid="join-share-code">
          {joinCode}
        </strong>
        <span className="join-share__url" data-testid="join-share-url">
          {url}
        </span>
        {canCopy() && (
          <button
            type="button"
            className="btn join-share__copy"
            data-testid="copy-join-link"
            onClick={onCopy}
            // Announce the transient confirmation to screen readers when the label
            // flips to "Copied!" (the only visual feedback otherwise).
            aria-live="polite"
          >
            {copied ? 'Copied!' : 'Copy link'}
          </button>
        )}
      </div>
    </div>
  );
}
